/**
 * B-W4 (founder decision 2026-10-18): THE planning-buffer rule, for the browser.
 *
 *   1. A recorded buffer percentage is used as recorded (clamped to 0..100).
 *   2. An unrecorded buffer (NULL / undefined / not a number) is planned as 0 %.
 *   3. A recorded 0 stays 0 (so neither `?? 25` nor `|| 25` is allowed anywhere).
 *   4. There is no hidden 25 % default.
 *
 * This module does NOT re-implement the rule: it re-exports the server's resolver
 * (supabase/functions/_shared/schedulingDay.ts `resolvePlanningBufferPct`), so the Dayboard,
 * ticket cards, estimates and every scheduling consumer plan with exactly the number the server
 * finish authority plans with. tests/unit/scheduling/bufferPolicyB_W4.test.ts pins the parity and
 * tests/unit/scheduling/bufferFallbackGuard.test.ts fails if a `buffer … ?? 25` fallback reappears.
 *
 * Where it matters operationally (technician / shop settings displays) "no buffer set" is shown as
 * such, never as a fake percentage: use `formatPlanningBufferLabel`.
 */
import { resolvePlanningBufferPct } from '../../supabase/functions/_shared/schedulingDay';

export { resolvePlanningBufferPct };

export const NO_BUFFER_SET_LABEL = 'No buffer set';

/** True only when a buffer percentage has actually been recorded (0 counts as recorded). */
export function isPlanningBufferConfigured(bufferPct: unknown): bufferPct is number {
  return typeof bufferPct === 'number' && Number.isFinite(bufferPct);
}

/** Display label: the recorded percentage (e.g. "0%", "10%"), or "No buffer set". */
export function formatPlanningBufferLabel(bufferPct: number | null | undefined): string {
  return isPlanningBufferConfigured(bufferPct) ? `${resolvePlanningBufferPct(bufferPct)}%` : NO_BUFFER_SET_LABEL;
}

/** Capacity-side reserve as a fraction (0..1) — the same rule, for `capacity × (1 − reserve)` readers. */
export function planningBufferFraction(bufferPct: number | null | undefined): number {
  return resolvePlanningBufferPct(bufferPct) / 100;
}
